import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DrawResultView, ResultEntry, ResultFormView, ResultInput } from '@lp/contracts';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { errorText, fieldProblems, get, post, put } from '@/lib/api';
import { useIsSuperAdmin } from '@/lib/auth';
import { dateTime } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { Badge, Button, Card, Dialog, ErrorBox, Field, Input, Loading, PageHeader } from '@/components/ui';
import { ResultStateBadge } from './results';

/** an empty form: every slot's boxes, every hand post's 2D and 3D */
function blank(form: ResultFormView): ResultInput {
  return {
    roles: Object.fromEntries(form.roles.map((r) => [r.role, Object.fromEntries(r.slots.map((s) => [s.key, Array.from({ length: s.count }, () => '')]))])),
    hand: Object.fromEntries(form.handPosts.map((p) => [p, { d2: '', d3: '' }])),
  };
}

/** what is sent: sheets with nothing typed are left out (the pattern decides whether it needs them) */
function cleaned(input: ResultInput): ResultInput {
  const roles = Object.fromEntries(Object.entries(input.roles).filter(([, slots]) => Object.values(slots).some((vs) => vs.some((v) => v.trim()))));
  return { roles, hand: input.hand };
}

/** a draw's result, post by post: "2D-3D" pairs, and Lo's 2D / 3D lists */
export function ResultEntries({ entries }: { entries: ResultEntry[] }) {
  const t = useT();
  if (!entries.length) return <p className="text-sm text-ink-600">{t('Nothing yet.')}</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-ink-100 text-left text-xs text-ink-500">
          <th className="w-20 py-1.5 pr-3">{t('Post')}</th>
          <th className="py-1.5 pr-3">{t('Result')}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e, i) => (
          <tr key={`${e.postCode}-${e.digitLen ?? ''}-${i}`} className="border-b border-ink-100 align-top last:border-0" data-testid={`entry-${e.postCode}${e.digitLen ? `-${e.digitLen}` : ''}`}>
            <td className="py-1.5 pr-3 font-mono font-semibold">
              {e.postCode}
              {e.digitLen && <span className="ml-1 text-xs font-normal text-ink-500">{e.digitLen}D</span>}
              {e.hand && <span className="ml-1 text-[10px] font-normal text-ink-400">✎</span>}
            </td>
            <td className="py-1.5 pr-3 font-mono">
              {e.numbers.map((n, j) => (
                <span key={j} className="mr-2 inline-block">
                  {n.includes('-') ? (
                    <>
                      <span className="font-semibold">{n.split('-')[0]}</span> · <span className="font-semibold">{n.split('-')[1]}</span>
                    </>
                  ) : (
                    n
                  )}
                </span>
              ))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** one draw's result: the form (sheets + posts typed by hand), check, save, publish, amend — and its history */
export function ResultPage() {
  const t = useT();
  const qc = useQueryClient();
  const superAdmin = useIsSuperAdmin();
  const { product = '', game = '', date = '', time = '' } = useParams();
  const path = `/platform/results/${product}/${game}/${date}/${time}`;
  const view = useQuery({ queryKey: ['result', path], queryFn: () => get<DrawResultView>(path) });
  const [input, setInput] = useState<ResultInput | null>(null);
  const [source, setSource] = useState<'manual' | 'paste'>('manual');
  const [checked, setChecked] = useState<{ entries: ResultEntry[]; notes: string[] } | null>(null);
  const [amending, setAmending] = useState(false);
  const [reason, setReason] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteNotes, setPasteNotes] = useState<string[]>([]);

  useEffect(() => {
    if (view.data && !input) setInput(view.data.inputs ? { ...blank(view.data.form), ...structuredClone(view.data.inputs), roles: { ...blank(view.data.form).roles, ...structuredClone(view.data.inputs.roles) } } : blank(view.data.form));
  }, [view.data, input]);

  const refresh = (v: DrawResultView) => {
    qc.setQueryData(['result', path], v);
    void qc.invalidateQueries({ queryKey: ['results'] });
    setChecked(null);
  };
  const check = useMutation({ mutationFn: () => post<{ entries: ResultEntry[]; notes: string[] }>(`${path}/preview`, { inputs: cleaned(input!) }), onSuccess: setChecked });
  const save = useMutation({ mutationFn: () => put<DrawResultView>(path, { inputs: cleaned(input!), source }), onSuccess: refresh });
  const publish = useMutation({
    mutationFn: () => post<DrawResultView>(`${path}/publish`),
    onSuccess: (v) => {
      setPublishing(false);
      refresh(v);
    },
  });
  const amend = useMutation({
    mutationFn: () => post<DrawResultView>(`${path}/amend`, { inputs: cleaned(input!), reason }),
    onSuccess: (v) => {
      setAmending(false);
      setReason('');
      refresh(v);
    },
  });
  const paste = useMutation({
    mutationFn: () => post<{ inputs: ResultInput; notes: string[] }>(`${path}/paste`, { text: pasteText }),
    onSuccess: (r) => {
      const b = blank(view.data!.form);
      setInput({ roles: { ...b.roles, ...r.inputs.roles }, hand: input?.hand ?? b.hand });
      setSource('paste');
      setPasteNotes(r.notes);
      setChecked(null);
    },
  });

  if (view.isLoading || (view.data && !input)) return <Loading />;
  if (view.error || !view.data || !input) return <ErrorBox text={errorText(view.error)} />;
  const v = view.data;
  const editable = superAdmin && (v.state !== 'published' || amending);
  const error = check.error ?? save.error ?? amend.error ?? paste.error;
  const setSlot = (role: string, key: string, i: number, value: string) =>
    setInput((cur) => {
      const next = structuredClone(cur!);
      next.roles[role]![key]![i] = value.replace(/\D/g, '');
      return next;
    });
  const setHand = (p: string, part: 'd2' | 'd3', value: string) =>
    setInput((cur) => {
      const next = structuredClone(cur!);
      next.hand[p] = { ...next.hand[p]!, [part]: value.replace(/\D/g, '') };
      return next;
    });

  return (
    <>
      <Link to={`/results?date=${v.datePP}`} className="mb-2 inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-800">
        <ArrowLeft size={12} /> {t('Results')} {v.datePP}
      </Link>
      <PageHeader
        title={`${v.productName} · ${v.gameName}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono">
              {v.datePP} {v.timePP}
            </span>
            {v.time !== v.timePP && (
              <span className="text-xs text-ink-500">
                ({v.date} {v.time} {t('local')})
              </span>
            )}
            <ResultStateBadge state={v.state} revision={v.revision} />
            <Badge>{v.pattern ? `${t('Pattern')}: ${v.pattern}` : t('Every post typed by hand')}</Badge>
          </span>
        }
        actions={
          superAdmin && (
            <>
              {v.state === 'draft' && (
                <Button onClick={() => setPublishing(true)} disabled={save.isPending}>
                  {t('Publish')}
                </Button>
              )}
              {v.state === 'published' && !amending && (
                <Button variant="secondary" onClick={() => setAmending(true)}>
                  {t('Amend')}
                </Button>
              )}
            </>
          )
        }
      />

      {editable && v.form.paste && (
        <Card title={t('Paste from a result page')} className="mb-4">
          <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={3} className="w-full rounded-md border border-ink-200 p-2 font-mono text-xs" placeholder={t('Paste the text of the official result page here')} aria-label={t('Paste from a result page')} />
          <div className="mt-2 flex items-center gap-2">
            <Button variant="secondary" onClick={() => paste.mutate()} disabled={pasteText.trim().length < 10 || paste.isPending}>
              {t('Read into the form')}
            </Button>
            {source === 'paste' && <Badge tone="blue">{t('Filled from a paste — check it')}</Badge>}
          </div>
          {pasteNotes.length > 0 && <ul className="mt-2 list-disc pl-5 text-xs text-amber-800">{pasteNotes.map((n, i) => <li key={i}>{n}</li>)}</ul>}
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-5">
        <div className="space-y-4 lg:col-span-3">
          {v.form.roles.map((r) => (
            <Card key={r.role} title={`${r.label} (${r.role})`}>
              <div className="space-y-2">
                {r.slots.map((s) => (
                  <div key={s.key} className="flex flex-wrap items-center gap-2">
                    <span className="w-28 shrink-0 text-xs text-ink-600">{s.label ?? s.key}</span>
                    {(input.roles[r.role]?.[s.key] ?? []).map((val, i) => (
                      <Input
                        key={i}
                        value={val}
                        onChange={(e) => setSlot(r.role, s.key, i, e.target.value)}
                        maxLength={s.digits}
                        inputMode="numeric"
                        disabled={!editable}
                        placeholder={'0'.repeat(s.digits)}
                        className="h-8 font-mono text-sm"
                        style={{ width: `${s.digits * 0.75 + 1.4}rem` }}
                        aria-label={`${r.role} ${s.label ?? s.key} ${i + 1}`}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </Card>
          ))}
          {v.form.handPosts.length > 0 && (
            <Card title={t('Posts typed by hand')}>
              <div className="grid grid-cols-[auto_auto_auto] items-center gap-x-3 gap-y-2 sm:w-max">
                <span />
                <span className="text-xs text-ink-500">2D</span>
                <span className="text-xs text-ink-500">3D</span>
                {v.form.handPosts.map((p) => (
                  <div key={p} className="contents">
                    <span className="font-mono font-semibold">{p}</span>
                    <Input value={input.hand[p]?.d2 ?? ''} onChange={(e) => setHand(p, 'd2', e.target.value)} maxLength={2} inputMode="numeric" disabled={!editable} placeholder="00" className="h-8 w-16 font-mono" aria-label={`${p} 2D`} />
                    <Input value={input.hand[p]?.d3 ?? ''} onChange={(e) => setHand(p, 'd3', e.target.value)} maxLength={3} inputMode="numeric" disabled={!editable} placeholder="000" className="h-8 w-20 font-mono" aria-label={`${p} 3D`} />
                  </div>
                ))}
              </div>
            </Card>
          )}
          {editable && (
            <div className="flex flex-wrap items-end gap-2">
              <Button variant="secondary" onClick={() => check.mutate()} disabled={check.isPending}>
                {t('Check')}
              </Button>
              {amending ? (
                <>
                  <Field label={t('Reason for the amendment')}>
                    <Input value={reason} onChange={(e) => setReason(e.target.value)} className="w-72" maxLength={300} />
                  </Field>
                  <Button onClick={() => amend.mutate()} disabled={reason.trim().length < 3 || amend.isPending}>
                    {t('Save the amendment')}
                  </Button>
                  <Button variant="ghost" onClick={() => setAmending(false)}>
                    {t('Cancel')}
                  </Button>
                </>
              ) : (
                <Button onClick={() => save.mutate()} disabled={save.isPending}>
                  {t('Save draft')}
                </Button>
              )}
            </div>
          )}
          {error && <ErrorBox text={[errorText(error), ...fieldProblems(error)].join(' — ')} />}
        </div>

        <div className="space-y-4 lg:col-span-2">
          <Card title={checked ? t('Result of what is typed (not saved)') : t('Result')}>
            <ResultEntries entries={checked?.entries ?? v.entries} />
            {(checked?.notes ?? v.notes).length > 0 && <ul className="mt-2 list-disc pl-5 text-xs text-ink-600">{(checked?.notes ?? v.notes).map((n, i) => <li key={i}>{n}</li>)}</ul>}
          </Card>
          <Card title={t('History')}>
            {!v.history.length ? (
              <p className="text-sm text-ink-600">{t('Nothing yet.')}</p>
            ) : (
              <ul className="divide-y divide-ink-100 text-sm">
                {v.history.map((h, i) => (
                  <li key={i} className="py-1.5">
                    <span className="mr-2 text-xs text-ink-500">{dateTime(h.at)}</span>
                    <Badge tone={h.action === 'publish' ? 'green' : h.action === 'amend' ? 'blue' : 'gray'}>{t(h.action === 'enter' ? 'Entered' : h.action === 'publish' ? 'Published' : 'Amended')}</Badge>
                    <span className="ml-2 text-xs">{h.by ?? '—'}</span>
                    {h.reason && <span className="block text-xs text-ink-600">{h.reason}</span>}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      {publishing && (
        <Dialog
          title={`${t('Publish')} — ${v.productCode} ${v.gameCode} ${v.datePP} ${v.timePP}`}
          onClose={() => setPublishing(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setPublishing(false)}>
                {t('Cancel')}
              </Button>
              <Button onClick={() => publish.mutate()} disabled={publish.isPending}>
                {t('Publish')}
              </Button>
            </>
          }
        >
          <p>{t('This is the result that counts. Check it one more time:')}</p>
          <ResultEntries entries={v.entries} />
          {publish.error && <ErrorBox text={errorText(publish.error)} />}
        </Dialog>
      )}
    </>
  );
}
